import React, { useCallback, useRef, useState } from 'react';
import { useAwaitControl } from 'react-redux-await-control';

import { NavbarItem, SubItem, Submenu, SubmenuWrapper } from '~/ui/components/Menu/Menu.styled';
import { getLoggedUser, logout } from '~/store/user/actions';
import { useModal } from '~/hooks/useModal';
import { Connection, Logout, Settings as SettingsIcon, User, UserPlus } from '~/ui/assets/icons';
import { EnvironmentModal } from '~/components/EnvironmentModal/EnvironmentModal';
import { CreateUserModal } from '~/components/CreateUserModal/CreateUserModal';
import useUserRoles from '~/hooks/useUserRoles';

import useOutsideClickEffect from '~/common/useOutsideClickEffect';
import { Container, SettingsButton } from './Settings.styled';

type SettingsProps = {
  trigger?: 'hover' | 'click';
};

export function Settings({ trigger = 'hover' }: SettingsProps) {
  const containerRef = useRef(null);
  const { openModal } = useModal();
  const [opened, setOpened] = useState(false);
  const [closing, setClosing] = useState(false);
  const { isAdmin, hasRole } = useUserRoles();
  const logoutControl = useAwaitControl(logout);

  const loggedUserControl = useAwaitControl(getLoggedUser);
  const loggedUser = loggedUserControl.result();

  const showUsersTableAccess = hasRole('VIEW_USERS_PAGE');
  const showDistributionSettings = hasRole('AUTO_DISTRIBUTION_ADMIN') || hasRole('AUTO_DISTRIBUTION_VIEWER');

  const handleLogout = useCallback(
    (e) => {
      e.preventDefault();
      logoutControl.start({ userId: loggedUser?.id, userName: loggedUser?.name });
    },
    [loggedUser],
  );

  const close = () => {
    setClosing(true);
    setTimeout(() => {
      setOpened(false);
      setClosing(false);
    }, 300);
  };

  const handleAddUser = useCallback(() => {
    close();
  }, []);

  const handleChangeEnvironment = useCallback(
    (e) => {
      e?.preventDefault();
      close();
      openModal(<EnvironmentModal />, {
        id: 'environment',
        title: 'Alterar ambiente',
        closable: true,
        width: 555,
      });
    },
    [openModal],
  );

  const handleCreateUser = useCallback(
    (e) => {
      e?.preventDefault();
      close();
      openModal(<CreateUserModal />, {
        id: 'create-user',
        title: 'Criar novo usuário',
        closable: true,
        width: 400,
      });
    },
    [openModal],
  );

  const handleItemClick = useCallback(() => {
    if (trigger === 'click') {
      setOpened(true);
    }
  }, [opened]);

  useOutsideClickEffect(containerRef, () => {
    if (trigger === 'click' && opened) {
      close();
    }
  });

  return (
    <Container trigger={trigger} ref={containerRef}>
      <NavbarItem
        className={`nav-item ${opened ? 'open' : ''} ${closing ? 'closing' : ''}`}
        onClick={handleItemClick}
        trigger={trigger}
      >
        <SettingsButton data-testid="nav-bar-settings-button" />

        <SubmenuWrapper>
          <Submenu>
            {isAdmin && (
              <SubItem to="/" onClick={handleCreateUser}>
                <UserPlus />
                Criar novo usuário
              </SubItem>
            )}
            {showUsersTableAccess && (
              <SubItem to="/backoffice/users" onClick={handleAddUser}>
                <User />
                Adicionar ou editar acessos
              </SubItem>
            )}

            {showDistributionSettings && (
              <SubItem to="/backoffice/settings/resolving-areas" onClick={close}>
                <SettingsIcon />
                Configurações
              </SubItem>
            )}

            <SubItem to="/" onClick={handleChangeEnvironment}>
              <Connection />
              Alterar ambiente
            </SubItem>

            <SubItem className="logout" to="/" onClick={handleLogout}>
              <Logout />
              Sair
            </SubItem>
          </Submenu>
        </SubmenuWrapper>
      </NavbarItem>
    </Container>
  );
}
